/* ============================================================
   A2 — Cost-per-Ton-Km Benchmarking Calculator

   Audit fixes applied here:

   A-05  The mode list is derived from the corridor's published bands.
         "Multimodal" was previously offered with no data behind it, so
         selecting it silently produced a bare number with no comparison
         and no explanation. Unbenchmarked modes now say why.

   A-15  The gauge marker is positioned by gaugePosition(), which pins
         median to the midpoint. Previously it interpolated linearly
         between low and high while the labels keyed off the median, so
         a cost at median could sit left of centre and read "good".

   A-16  Fuel sensitivity is suppressed when no fuel cost was entered,
         instead of printing three identical rows.

   A-17  A blank payload no longer silently defaults to 1 tonne, which
         inflated cost per ton-km by roughly 28×. Inputs are validated
         and the calculation is blocked with a visible reason.
   ============================================================ */

import {
  getBenchmark,
  getAllCorridors,
  availableModes,
  getBand,
  compareToBenchmark,
  gaugePosition,
  DEFAULT_PAYLOAD_TONNES
} from '../../data/benchmarks.js';
import { html, raw } from '../../shared/html.js';
import { icon } from '../../shared/icons.js';
import { money, number, signedPercent, parseNumber, shortDate } from '../../shared/format.js';
import { saveRecord } from '../../shared/records.js';
import { prefillForm, rememberProfile } from '../../shared/profile.js';
import { PrintButton } from '../../components/PrintButton.js';
import { PrefillNotice } from '../../components/PrefillNotice.js';
import { readable } from '../../shared/tone.js';

const COST_FIELDS = [
  { id: 'fuel', label: 'Fuel', color: 'var(--ca-chart-8)' },
  { id: 'driver', label: 'Driver wages', color: 'var(--ca-chart-2)' },
  { id: 'tolls', label: 'Tolls & levies', color: 'var(--ca-chart-1)' },
  { id: 'maintenance', label: 'Maintenance', color: 'var(--ca-chart-3)' },
  { id: 'insurance', label: 'Insurance', color: 'var(--ca-chart-4)' },
  { id: 'financing', label: 'Financing', color: 'var(--ca-chart-6)' },
  { id: 'port', label: 'Port / ICD charges', color: 'var(--ca-chart-5)' },
  { id: 'other', label: 'Other', color: 'var(--ca-chart-7)' }
];

const MODE_LABELS = { road: 'Road', rail: 'Rail (SGR)' };
/** Lower-case forms for running prose, so we never emit "rail (sgr)". */
const MODE_NOUNS = { road: 'road', rail: 'rail' };

export function BenchmarkPage() {
  const page = document.createElement('div');
  page.className = 'container-narrow page animate-fade-in';

  page.innerHTML = html`
    <header class="page-head">
      <h1>Cost-per-Ton-Km Calculator</h1>
      <p>
        Work out what a lane actually costs you, then compare it against sourced Kenyan
        benchmarks. Every band shows where it came from.
      </p>
    </header>

    <div id="benchmark-form">
      <section class="section">
        <h2 class="section-title">Route &amp; mode</h2>
        <div class="card">
          <label class="field">
            <span class="field-label">Corridor</span>
            <select id="corridor" class="input-field">
              <option value="">Select a corridor…</option>
            </select>
          </label>

          <div class="grid-2">
            <label class="field">
              <span class="field-label">Distance (km)</span>
              <input id="distance" class="input-field num" type="number" inputmode="numeric"
                     min="1" step="1" placeholder="485" />
            </label>
            <label class="field">
              <span class="field-label">Mode</span>
              <select id="mode" class="input-field"></select>
            </label>
          </div>

          <label class="field" style="margin-bottom: 0;">
            <span class="field-label">Payload (tonnes)</span>
            <input id="payload" class="input-field num" type="number" inputmode="decimal"
                   min="0.1" step="0.1" placeholder="${DEFAULT_PAYLOAD_TONNES}" />
            <span class="field-hint" id="payload-hint">
              The billed or actual weight moved. Required — the result is per tonne-kilometre.
            </span>
          </label>
          <div id="mode-note"></div>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">Cost components (USD, per trip)</h2>
        <div class="card">
          <div class="grid-2">
            ${COST_FIELDS.map(
              (f) => html`
                <label class="field">
                  <span class="field-label" style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
                    ${f.label}
                  </span>
                  <input id="cost-${f.id}" class="input-field num cost-input" type="number"
                         inputmode="decimal" min="0" step="1" placeholder="0" />
                </label>
              `
            )}
          </div>
        </div>
      </section>

      <div id="form-error"></div>

      <button class="btn btn-primary btn-block" id="calculate" type="button"
              style="padding: var(--ca-space-4);">
        Calculate &amp; compare
      </button>
    </div>

    <div id="benchmark-results" hidden></div>
  `;

  const corridorSelect = page.querySelector('#corridor');
  const modeSelect = page.querySelector('#mode');
  const distanceInput = page.querySelector('#distance');
  const modeNote = page.querySelector('#mode-note');

  for (const corridor of getAllCorridors()) {
    const option = document.createElement('option');
    option.value = corridor.key;
    option.textContent = `${corridor.name} · ${corridor.distance} km`;
    corridorSelect.appendChild(option);
  }

  function refreshModes() {
    const key = corridorSelect.value;
    const modes = key ? availableModes(key) : [];
    const previous = modeSelect.value;

    modeSelect.replaceChildren();

    if (modes.length === 0) {
      const option = document.createElement('option');
      option.value = '';
      option.textContent = 'Select a corridor first';
      modeSelect.appendChild(option);
      modeSelect.disabled = true;
      modeNote.innerHTML = '';
      return;
    }

    modeSelect.disabled = false;
    for (const mode of modes) {
      const option = document.createElement('option');
      option.value = mode;
      option.textContent = MODE_LABELS[mode] || mode;
      modeSelect.appendChild(option);
    }
    if (modes.includes(previous)) modeSelect.value = previous;

    const bench = getBenchmark(key);
    const missing = ['road', 'rail'].filter((m) => !modes.includes(m));
    modeNote.innerHTML = missing.length
      ? html`<div class="notice notice-info" style="margin-top: var(--ca-space-3);">
          <span style="flex-shrink:0;margin-top:2px;">${raw(icon('info', 15))}</span>
          <div style="font-size: var(--ca-text-xs);">
            No ${missing.map((m) => MODE_NOUNS[m] || m).join(' or ')} band is published for
            ${bench.name}, so that mode is not offered. ${bench.notes}
          </div>
        </div>`
      : '';
  }

  corridorSelect.addEventListener('change', () => {
    const bench = getBenchmark(corridorSelect.value);
    if (bench) distanceInput.value = String(bench.distance);
    refreshModes();
  });

  refreshModes();

  // Corridor, distance and payload are the same three facts the
  // profitability and emissions tools ask for. Fill any that are still
  // blank, then say so — silence here would be an unannounced
  // assumption sitting inside the answer.
  const applied = prefillForm(page, {
    corridor: 'corridor',
    distance: 'distance',
    payload: 'payload'
  });
  if (applied.length > 0) {
    refreshModes();
    const notice = PrefillNotice(applied, page);
    if (notice) page.querySelector('#benchmark-form').prepend(notice);
  }

  page.querySelector('#calculate').addEventListener('click', () => calculate(page));

  return page;
}

/* ============================================================
   Calculation
   ============================================================ */

function readInputs(page) {
  const corridorKey = page.querySelector('#corridor').value;
  const mode = page.querySelector('#mode').value;
  const distance = parseNumber(page.querySelector('#distance').value);
  const payload = parseNumber(page.querySelector('#payload').value);

  const costs = {};
  for (const field of COST_FIELDS) {
    costs[field.id] = parseNumber(page.querySelector(`#cost-${field.id}`).value) ?? 0;
  }

  const errors = [];
  if (!corridorKey) errors.push('Select a corridor.');
  if (!mode) errors.push('Select a mode.');
  // A-17: no silent default. A blank payload is an error, not a 1.
  if (distance === null || distance <= 0) errors.push('Enter a distance greater than zero.');
  if (payload === null || payload <= 0) errors.push('Enter a payload greater than zero.');
  if (Object.values(costs).every((v) => v === 0)) errors.push('Enter at least one cost component.');

  return { corridorKey, mode, distance, payload, costs, errors };
}

function calculate(page) {
  const input = readInputs(page);
  const errorSlot = page.querySelector('#form-error');

  if (input.errors.length > 0) {
    errorSlot.innerHTML = html`
      <div class="notice notice-danger" style="margin-bottom: var(--ca-space-4);">
        <span style="flex-shrink:0;margin-top:2px;">${raw(icon('alert', 15))}</span>
        <div>
          <strong style="font-weight: var(--ca-weight-medium);">Cannot calculate yet</strong>
          <ul style="margin: var(--ca-space-1) 0 0 var(--ca-space-4); font-size: var(--ca-text-xs);">
            ${input.errors.map((e) => html`<li>${e}</li>`)}
          </ul>
        </div>
      </div>
    `;
    errorSlot.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    return;
  }

  errorSlot.innerHTML = '';

  const totalCost = Object.values(input.costs).reduce((a, b) => a + b, 0);
  const tonKm = input.payload * input.distance;
  const costPerTonKm = totalCost / tonKm;

  const comparison = compareToBenchmark(input.corridorKey, input.mode, costPerTonKm);
  const bench = getBenchmark(input.corridorKey);
  const band = getBand(input.corridorKey, input.mode);

  renderResults(page, { ...input, totalCost, tonKm, costPerTonKm, comparison, bench, band });

  void saveRecord('benchmark', {
    corridor: input.corridorKey,
    mode: input.mode,
    distance: input.distance,
    payload: input.payload,
    costPerTonKm,
    totalCost
  });

  rememberProfile({
    corridor: input.corridorKey,
    mode: input.mode,
    distance: input.distance,
    payload: input.payload
  });
}

/* ============================================================
   Results
   ============================================================ */

function renderResults(page, r) {
  page.querySelector('#benchmark-form').hidden = true;

  const results = page.querySelector('#benchmark-results');
  results.hidden = false;
  results.className = 'animate-fade-in';
  results.replaceChildren();

  const statusLabel = r.comparison.available ? r.comparison.status.toUpperCase() : 'CALCULATED';
  const tone = r.comparison.available ? r.comparison.tone : 'var(--ca-text-quaternary)';

  /* ---- Headline ---- */
  const head = document.createElement('div');
  head.style.cssText = 'text-align:center; margin-bottom: var(--ca-space-6);';
  head.innerHTML = html`
    <div class="badge" style="background: color-mix(in srgb, ${tone} 12%, transparent); color: ${readable(tone)};">
      ${statusLabel}
    </div>
    <div class="stat-figure num" style="margin-top: var(--ca-space-3);">
      $${r.costPerTonKm.toFixed(3)}
    </div>
    <div style="font-size: var(--ca-text-sm); color: var(--ca-text-tertiary); margin-top: var(--ca-space-1);">
      per tonne-kilometre · ${MODE_LABELS[r.mode] || r.mode} ·
      <span class="num">${number(r.distance)} km</span> ·
      <span class="num">${number(r.payload, 1)} t</span>
    </div>
    <div style="font-size: var(--ca-text-xs); color: var(--ca-text-quaternary); margin-top: var(--ca-space-1);">
      ${money(r.totalCost)} total over <span class="num">${number(r.tonKm)}</span> tonne-km
    </div>
  `;
  results.appendChild(head);

  /* ---- Breakdown ---- */
  const items = COST_FIELDS.map((f) => ({ ...f, value: r.costs[f.id] })).filter((c) => c.value > 0);
  const breakdown = document.createElement('div');
  breakdown.className = 'card';
  breakdown.style.marginBottom = 'var(--ca-space-4)';
  breakdown.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-3);">Cost breakdown</h3>
    <div class="stack" style="gap: var(--ca-space-2);">
      ${items.map(
        (c) => html`
          <div class="row">
            <span style="width:10px;height:10px;border-radius:2px;background:${c.color};flex-shrink:0;"></span>
            <span style="flex:1; font-size: var(--ca-text-sm);">${c.label}</span>
            <span class="num" style="font-size: var(--ca-text-sm); font-weight: var(--ca-weight-medium);">
              ${money(c.value)}
            </span>
            <span class="num" style="width:44px; text-align:right; font-size: var(--ca-text-xs); color: var(--ca-text-quaternary);">
              ${((c.value / r.totalCost) * 100).toFixed(0)}%
            </span>
          </div>
        `
      )}
    </div>
    <div class="row" style="border-top:1px solid var(--ca-border); margin-top: var(--ca-space-3); padding-top: var(--ca-space-3); font-weight: var(--ca-weight-medium);">
      <span style="flex:1;">Total</span>
      <span class="num">${money(r.totalCost)}</span>
    </div>
  `;
  results.appendChild(breakdown);

  /* ---- Benchmark ---- */
  if (r.comparison.available && r.band) {
    const position = gaugePosition(r.costPerTonKm, r.band);
    const gauge = document.createElement('div');
    gauge.className = 'card';
    gauge.style.marginBottom = 'var(--ca-space-4)';
    gauge.innerHTML = html`
      <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-4);">
        Against ${r.bench.name} · ${MODE_LABELS[r.mode] || r.mode}
      </h3>
      <div style="position:relative; height:44px; margin-bottom: var(--ca-space-3);">
        <div style="position:absolute; top:50%; left:0; right:0; height:8px; border-radius:var(--ca-radius-pill);
                    background: linear-gradient(to right, var(--ca-positive) 0%, var(--ca-warning) 50%, var(--ca-danger) 100%);
                    transform: translateY(-50%);"></div>
        <div style="position:absolute; top:50%; left:50%; width:1px; height:16px; background: var(--ca-surface);
                    transform: translate(-50%, -50%); opacity:.6;"></div>
        <div style="position:absolute; top:0; left:${position}%; transform:translateX(-50%); text-align:center;">
          <div style="width:13px;height:13px;background:var(--ca-text-primary);border-radius:50%;
                      margin:0 auto;border:2.5px solid var(--ca-surface);box-shadow: var(--ca-shadow-sm);"></div>
          <div style="font-size:var(--ca-text-2xs);font-weight:var(--ca-weight-medium);margin-top:3px;white-space:nowrap;">You</div>
        </div>
      </div>
      <div class="row" style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
        <span class="num">Low $${r.band.low.toFixed(3)}</span>
        <span class="spacer"></span>
        <span class="num">Median $${r.band.median.toFixed(3)}</span>
        <span class="spacer"></span>
        <span class="num">High $${r.band.high.toFixed(3)}</span>
      </div>
      <div style="margin-top: var(--ca-space-4); padding-top: var(--ca-space-3); border-top:1px solid var(--ca-border);
                  font-size: var(--ca-text-xs); color: var(--ca-text-quaternary); line-height: var(--ca-leading-snug);">
        <strong style="font-weight: var(--ca-weight-medium); color: var(--ca-text-tertiary);">
          Where this band comes from
        </strong><br />
        ${r.band.basis}<br />
        Assumes ${r.band.payloadTonnes} t payload · ${r.band.provenance.replace('-', ' ')} ·
        ${r.bench.measuredAt
          ? `measured ${r.bench.measuredAt}, checked ${shortDate(r.bench.asOf)}`
          : `checked ${shortDate(r.bench.asOf)}; vintage of the underlying source not recorded`}
        · ${r.bench.source}
      </div>
    `;
    results.appendChild(gauge);

    if (r.band.payloadTonnes !== r.payload) {
      const caveat = document.createElement('div');
      caveat.className = 'notice notice-info';
      caveat.style.marginBottom = 'var(--ca-space-4)';
      caveat.innerHTML = html`
        <span style="flex-shrink:0;margin-top:2px;">${raw(icon('info', 15))}</span>
        <div style="font-size: var(--ca-text-xs);">
          The band assumes a ${r.band.payloadTonnes} t payload and you entered
          ${number(r.payload, 1)} t. Per-tonne-km cost falls as payload rises, so a lighter load
          will read worse than the band and a heavier one better. Compare like with like.
        </div>
      `;
      results.appendChild(caveat);
    }
  } else {
    const unavailable = document.createElement('div');
    unavailable.className = 'notice notice-info';
    unavailable.style.marginBottom = 'var(--ca-space-4)';
    unavailable.innerHTML = html`
      <span style="flex-shrink:0;margin-top:2px;">${raw(icon('info', 15))}</span>
      <div style="font-size: var(--ca-text-xs);">
        <strong style="font-weight: var(--ca-weight-medium);">No benchmark comparison</strong><br />
        ${r.comparison.reason} Your cost per tonne-km above is still correct — there is simply
        nothing sourced to compare it against on this lane and mode.
      </div>
    `;
    results.appendChild(unavailable);
  }

  /* ---- Sensitivity (A-16) ---- */
  if (r.costs.fuel > 0) {
    const scenarios = [
      { label: 'Fuel −10%', factor: 0.9 },
      { label: 'Fuel +10%', factor: 1.1 },
      { label: 'Fuel +25%', factor: 1.25 }
    ];

    const sensitivity = document.createElement('div');
    sensitivity.className = 'card';
    sensitivity.style.marginBottom = 'var(--ca-space-4)';
    sensitivity.innerHTML = html`
      <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-3);">
        Sensitivity to fuel price
      </h3>
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Scenario</th>
              <th class="right">Cost/tonne-km</th>
              <th class="right">Change</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Current</td>
              <td class="right num" style="font-weight: var(--ca-weight-medium);">
                $${r.costPerTonKm.toFixed(3)}
              </td>
              <td class="right">—</td>
            </tr>
            ${scenarios.map((s) => {
              const newTotal = r.totalCost - r.costs.fuel + r.costs.fuel * s.factor;
              const newCost = newTotal / r.tonKm;
              const delta = ((newCost - r.costPerTonKm) / r.costPerTonKm) * 100;
              const color = delta > 0 ? 'var(--ca-danger)' : 'var(--ca-positive)';
              return html`
                <tr>
                  <td>${s.label}</td>
                  <td class="right num">$${newCost.toFixed(3)}</td>
                  <td class="right num" style="color:${readable(color)};">${signedPercent(delta)}</td>
                </tr>
              `;
            })}
          </tbody>
        </table>
      </div>
      <p style="font-size: var(--ca-text-xs); color: var(--ca-text-quaternary); margin-top: var(--ca-space-3);">
        Fuel is ${((r.costs.fuel / r.totalCost) * 100).toFixed(0)}% of your entered cost. The
        Kenya Transporters Association puts the road-freight norm at roughly 55%.
      </p>
    `;
    results.appendChild(sensitivity);
  }

  /* ---- Interpretation ---- */
  const readOut = {
    excellent: 'Below the published low band. Worth documenting how, because it is a real advantage.',
    good: 'Between low and median. Competitive; look for incremental gains rather than restructuring.',
    fair: 'Between median and high. Room to move — start with fuel procurement and empty running.',
    poor: 'Above the high band. Examine fuel, backhaul and whether rail line-haul fits this cargo.'
  };

  const note = document.createElement('div');
  note.className = 'card card-tinted';
  note.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-2);">What this means</h3>
    <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
      ${r.comparison.available
        ? readOut[r.comparison.status]
        : 'Enter a corridor and mode that carry a published band to see how you compare.'}
    </p>
  `;
  results.appendChild(note);

  results.appendChild(
    PrintButton({
      targetId: 'benchmark-results',
      title: 'Cost-per-Ton-Km Analysis',
      subtitle: `${r.bench ? r.bench.name : 'Custom lane'} · ${MODE_LABELS[r.mode] || r.mode}`
    })
  );

  const again = document.createElement('div');
  again.className = 'no-print';
  again.style.cssText = 'text-align:center; margin-top: var(--ca-space-4);';
  const btn = document.createElement('button');
  btn.className = 'btn btn-ghost';
  btn.type = 'button';
  btn.textContent = 'Recalculate';
  btn.addEventListener('click', () => {
    results.hidden = true;
    page.querySelector('#benchmark-form').hidden = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
  again.appendChild(btn);
  results.appendChild(again);

  results.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
